import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NICE = { "persons.assigned_recruiter_id": "assigned candidates", "recruiting_opportunities.assigned_recruiter_id": "opportunities",
  "tasks.assignee_id": "tasks", "appointments.assigned_recruiter_id": "meetings", "projects.owner_id": "projects" };
const fmt = (o) => Object.entries(o || {}).map(([k, n]) => `${NICE[k] || k.split(".")[0].replace(/_/g, " ")}: ${n}`).join(" · ") || "none";

export default function DemoCleanup() {
  const { isSuper } = useAuth();
  if (!isSuper) return <p className="text-sm text-slate-500" data-testid="demo-cleanup-denied">Remove Demo Users is available to Super Admins only.</p>;
  return <DemoCleanupInner />;
}

function DemoCleanupInner() {
  const [rep, setRep] = useState(null);
  const [done, setDone] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (apply) => {
    setBusy(true);
    try {
      const r = (await api.post(`/admin/demo-cleanup/${apply ? "apply" : "preview"}`)).data;
      if (apply) { setDone(r); setRep(null); setText(""); toast.success(`Removed ${r.user_count} demo users and ${r.person_count} demo people`); }
      else { setRep(r); setDone(null); }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5 max-w-5xl" data-testid="demo-cleanup-page">
      <div><h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><Trash2 className="w-6 h-6 text-rose-600" />Remove Demo Users</h1>
        <p className="text-sm text-slate-500 mt-1">Permanently deletes every login with an @demo. email (roles, invitations, sessions) and every Person with an @demo. email, with their tasks, opportunities, notes, documents and meetings. Real records assigned to a demo user are kept and set to Unassigned. Preview first.</p></div>
      <Button variant="outline" onClick={() => run(false)} disabled={busy} data-testid="dc-preview">Preview</Button>
      {done && <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="dc-done">Done. Logins removed: {done.user_count} · People removed: {done.person_count} · Unassigned: {fmt(done.unassigned)}</p>}
      {rep && (
        <div className="space-y-4" data-testid="dc-report">
          <p className="text-sm text-slate-700" data-testid="dc-counts"><b>{rep.users.length}</b> demo logins · <b>{rep.persons.length}</b> demo people · their records: {fmt(rep.person_records)} · will set to Unassigned: {fmt(rep.will_unassign)}</p>
          <div className="grid md:grid-cols-2 gap-4">
            <List title="Logins" rows={rep.users.map((u) => [u.name, u.email])} testId="dc-users" />
            <List title="People" rows={rep.persons.map((p) => [`${p.first_name || ""} ${p.last_name || ""}`.trim(), `${p.email}${p.human_id ? ` · ${p.human_id}` : ""}`])} testId="dc-persons" />
          </div>
          {(rep.users.length > 0 || rep.persons.length > 0) && (
            <div className="flex flex-wrap items-center gap-2">
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type DELETE to confirm" className="w-56" data-testid="dc-confirm-input" />
              <Button onClick={() => run(true)} disabled={busy || text !== "DELETE"} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="dc-apply">Delete permanently</Button>
            </div>)}
        </div>)}
    </div>
  );
}

function List({ title, rows, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={testId}>
      <p className="px-3 py-2 text-xs font-semibold uppercase text-slate-500 border-b border-slate-100">{title} ({rows.length})</p>
      <ul className="max-h-80 overflow-y-auto divide-y divide-slate-100">
        {rows.length ? rows.map(([a, b], i) => <li key={i} className="px-3 py-1.5"><span className="text-sm text-slate-800">{a || "—"}</span><span className="block text-[11px] text-slate-400">{b}</span></li>)
          : <li className="px-3 py-2 text-xs text-slate-400">None found.</li>}
      </ul>
    </div>
  );
}
